import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import AddPlayerModal from "./AddPlayerModal";
import { FaExchangeAlt, FaUserMinus, FaEdit } from 'react-icons/fa';
import { FaEnvelope, FaQrcode } from "react-icons/fa6";
import useUserRoles from '../../hooks/useUserRoles';
import { Dropdown } from 'react-bootstrap';
import EditTeamModal from "./EditTeamModal";
import InvitePlayerModal from "./InvitePlayerModal";
import QRCodeModal from "./QRCodeModal";

function TeamManagerDetailPage({ teamId: propTeamId, onBack }) {
    const { teamId: paramTeamId } = useParams(); // If route param is present
    const teamId = propTeamId || paramTeamId;   // Fallback to prop or param

    const [teamDetails, setTeamDetails] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showAddModal, setShowAddModal] = useState(false);
    const [showTransferModal, setShowTransferModal] = useState(false);
    const [showRemoveModal, setShowRemoveModal] = useState(false);
    const [selectedPlayer, setSelectedPlayer] = useState(null);
    const { isCaptain } = useUserRoles();
    const [showEditModal, setShowEditModal] = useState(false);
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [showQRModal, setShowQRModal] = useState(false);


    useEffect(() => {
        const fetchTeamDetails = async () => {
            try {
                const response = await axios.get(`/api/teams/${teamId}/`, {
                    withCredentials: true,
                });
                setTeamDetails(response.data);
                setLoading(false);
            } catch (error) {
                console.error("Error fetching team details:", error);
                setError("Failed to load team details.");
                setLoading(false);
            }
        };
        fetchTeamDetails();
    }, [teamId]);

    const handlePlayerAdded = (updatedTeam) => {
        setTeamDetails(updatedTeam);
        setShowAddModal(false);
        setShowTransferModal(false);
        setShowRemoveModal(false);
    };

    const handlePlayerAction = (player, action) => {
        setSelectedPlayer(player);
        if (action === 'transfer') {
            setShowTransferModal(true);
        } else if (action === 'remove') {
            setShowRemoveModal(true);
        }
    };

    const handlePlayerRemoved = async () => {
        try {
            const response = await axios.delete(`/api/teams/${teamId}/players/`, {
                withCredentials: true,
                params: {
                    player_id: selectedPlayer.id
                }
            });
            setTeamDetails(response.data);
            setShowRemoveModal(false);
            setSelectedPlayer(null);
        } catch (error) {
            console.error('Error removing player:', error);
            setError('Failed to remove player from team');
        }
    };

    if (loading) {
        return <div className="container">Loading team details...</div>;
    }

    if (error) {
        return <div className="container text-danger">{error}</div>;
    }

    return (
        <>
            <div className="container">
                {/* Card START */}
                <div className="card mb-3 p-0">
                    <div
                        className="h-200px rounded-top"
                        style={{
                            backgroundColor: teamDetails.color || '#c93452',
                            backgroundPosition: "center",
                            backgroundSize: "cover",
                            backgroundRepeat: "no-repeat",
                        }}
                    ></div>

                    {/* Card body START */}
                    <div className="card-body py-0">
                        <div className="d-sm-flex align-items-start text-center text-sm-start">
                            <div>
                                <div className="avatar avatar-xxl mt-n5 mb-3">
                                    <img
                                        className="avatar-img rounded-circle border border-white border-3"
                                        src={
                                            teamDetails.logo ||
                                            `${process.env.PUBLIC_URL}/images/kb_static/KLOC_circle.svg`
                                        }
                                        alt={`${teamDetails.name} logo`}
                                        style={{ backgroundColor: "white" }}
                                    />
                                </div>
                            </div>
                            <div className="ms-sm-4 mt-sm-3">
                                <div className="row">
                                    <div className="col">
                                        <h3 className="mb-0">{teamDetails.name}</h3>
                                    </div>
                                </div>
                                <p className="mb-0 text-muted">
                                    {teamDetails.league_name ? `League: ${teamDetails.league_name}` : 'No league assigned'}
                                </p>
                                <p className="mb-0 text-muted">
                                    {teamDetails.color_name ? `Color: ${teamDetails.color_name}` : 'No color assigned'}
                                </p>
                            </div>
                            <div className="d-flex mt-3 justify-content-center ms-sm-auto">
                                {isCaptain && (
                                    <Dropdown>
                                        <Dropdown.Toggle bsPrefix="dropdown-icon" variant="light" id="profileAction2">
                                            <i className="bi bi-three-dots"></i>
                                        </Dropdown.Toggle>
                                        <Dropdown.Menu>
                                            <Dropdown.Item onClick={() => setShowEditModal(true)}>
                                                <FaEdit className="me-1" /> Edit Team
                                            </Dropdown.Item>
                                            <Dropdown.Item onClick={() => setShowInviteModal(true)}>
                                                <FaEnvelope className="me-1" /> Invite Players
                                            </Dropdown.Item>
                                            <Dropdown.Item onClick={() => setShowQRModal(true)}>
                                                <FaQrcode className="me-1" /> QR Code
                                            </Dropdown.Item>
                                        </Dropdown.Menu>
                                    </Dropdown>
                                )}
                            </div>
                        </div>
                    </div>
                    {/* Card body END */}
                </div>
                {/* Card END */}

                {/* Edit Team Modal */}
                <EditTeamModal
                    show={showEditModal}
                    handleClose={() => setShowEditModal(false)}
                    team={teamDetails}
                    onTeamUpdated={(updatedTeam) => {
                        setTeamDetails(updatedTeam);
                        setShowEditModal(false);
                    }}
                />

                {/* Invite Players Modal */}
                <InvitePlayerModal
                    show={showInviteModal}
                    handleClose={() => setShowInviteModal(false)}
                    team={teamDetails}
                    season={teamDetails?.current_season}
                />

                <QRCodeModal
                    show={showQRModal}
                    handleClose={() => setShowQRModal(false)}
                    team={teamDetails}
                    season={teamDetails?.current_season}
                />

                {/* Team Details */}
                <div className="card card-body mb-3">
                    <div className="row g-4">
                        {/* Team Stats */}
                        <div className="col-sm-6 col-lg-4">
                            <h5>Team Stats</h5>
                            <div className="d-flex justify-content-between mb-2">
                                <span>Wins:</span>
                                <span className="text-success">{teamDetails.wins}</span>
                            </div>
                            <div className="d-flex justify-content-between mb-2">
                                <span>Losses:</span>
                                <span className="text-danger">{teamDetails.losses}</span>
                            </div>
                            <div className="d-flex justify-content-between mb-2">
                                <span>Draws:</span>
                                <span className="text-warning">{teamDetails.draws}</span>
                            </div>
                            <div className="d-flex justify-content-between mb-2">
                                <span>Total Games:</span>
                                <span className="text-primary">{teamDetails.total_games}</span>
                            </div>
                        </div>

                        {/* Team Leadership */}
                        <div className="col-sm-6 col-lg-4">
                            <h5>Team Leadership</h5>
                            <div className="mb-3">
                                <h6>Captain</h6>
                                {teamDetails.captain_name ? (
                                    <div className="d-flex align-items-center">
                                        <img
                                            src={teamDetails.captain_picture || `${process.env.PUBLIC_URL}/images/kb_static/KLOC_circle.svg`}
                                            alt="Captain"
                                            className="rounded-circle me-2"
                                            style={{ width: '32px', height: '32px', objectFit: 'cover' }}
                                        />
                                        <span>{teamDetails.captain_name}</span>
                                    </div>
                                ) : (
                                    <p className="text-muted small">No captain assigned</p>
                                )}
                            </div>
                            <div>
                                <h6>Assistant Captain</h6>
                                {teamDetails.a_cap_name ? (
                                    <div className="d-flex align-items-center">
                                        <img
                                            src={teamDetails.a_cap_picture || `${process.env.PUBLIC_URL}/images/kb_static/KLOC_circle.svg`}
                                            alt="Assistant Captain"
                                            className="rounded-circle me-2"
                                            style={{ width: '32px', height: '32px', objectFit: 'cover' }}
                                        />
                                        <span>{teamDetails.a_cap_name}</span>
                                    </div>
                                ) : (
                                    <p className="text-muted small">No assistant captain assigned</p>
                                )}
                            </div>
                        </div>

                        {/* Team Players */}
                        <div className="col-12">
                            <div className="row mb-4">
                                <div className="col">
                                    <h2>Team Players</h2>
                                </div>
                                <div className="col-auto">
                                    <button
                                        className="btn btn-primary"
                                        onClick={() => setShowAddModal(true)}
                                    >
                                        Add Player
                                    </button>
                                </div>
                            </div>
                            <div className="row g-4">
                                {teamDetails.players?.map((player) => (
                                    <div key={player.id} className="col-sm-6 col-lg-4">
                                        <div className="card h-100">
                                            <div className="card-body">
                                                <div className="d-flex flex-column">
                                                    <div className="d-flex align-items-center mb-3">
                                                        <img
                                                            src={player.profile_picture || `${process.env.PUBLIC_URL}/images/kb_static/KLOC_circle.svg`}
                                                            alt={`${player.first_name} ${player.last_name}`}
                                                            className="rounded-circle me-3"
                                                            style={{ width: '60px', height: '60px', objectFit: 'cover' }}
                                                        />
                                                        <div className="flex-grow-1">
                                                            <h6 className="mb-1">{player.first_name} {player.last_name}</h6>
                                                            <small className="text-muted">
                                                                {teamDetails.captain === player.id ? 'Captain' :
                                                                    teamDetails.a_cap === player.id ? 'Assistant Captain' :
                                                                        'Player'}
                                                            </small>
                                                        </div>
                                                    </div>
                                                    <div className="d-flex justify-content-end gap-2 mt-2">
                                                        <button
                                                            className="btn btn-outline-primary btn-sm d-flex align-items-center gap-1"
                                                            onClick={() => handlePlayerAction(player, 'transfer')}
                                                            title="Transfer Player"
                                                        >
                                                            <FaExchangeAlt size={14} />
                                                            <span>Transfer</span>
                                                        </button>
                                                        <button
                                                            className="btn btn-outline-danger btn-sm d-flex align-items-center gap-1"
                                                            onClick={() => handlePlayerAction(player, 'remove')}
                                                            title="Remove Player"
                                                        >
                                                            <FaUserMinus size={14} />
                                                            <span>Remove</span>
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Add Player Modal */}
            <AddPlayerModal
                teamId={teamId}
                onPlayerAdded={handlePlayerAdded}
                mode="add"
                show={showAddModal}
                onHide={() => setShowAddModal(false)}
            />

            {/* Transfer Player Modal */}
            <AddPlayerModal
                teamId={teamId}
                onPlayerAdded={handlePlayerAdded}
                mode="transfer"
                show={showTransferModal}
                onHide={() => {
                    setShowTransferModal(false);
                    setSelectedPlayer(null);
                }}
                selectedPlayer={selectedPlayer}
            />

            {/* Remove Player Confirmation Modal */}
            <div className={`modal ${showRemoveModal ? 'show d-block' : 'd-none'}`}
                tabIndex="-1"
                style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                <div className="modal-dialog">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">Remove Player</h5>
                            <button type="button" className="btn-close" onClick={() => {
                                setShowRemoveModal(false);
                                setSelectedPlayer(null);
                            }}></button>
                        </div>
                        <div className="modal-body">
                            {selectedPlayer && (
                                <p>Are you sure you want to remove {selectedPlayer.first_name} {selectedPlayer.last_name} from the team?</p>
                            )}
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-secondary" onClick={() => {
                                setShowRemoveModal(false);
                                setSelectedPlayer(null);
                            }}>Cancel</button>
                            <button type="button" className="btn btn-danger" onClick={handlePlayerRemoved}>
                                Remove Player
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

export default TeamManagerDetailPage; 